import { Card, Divider, LinkButton } from '@onefootprint/ui';
import uniqBy from 'lodash/uniqBy';
import { useRouter } from 'next/router';
import { type CSSProperties, useDeferredValue, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CardError } from '@/components/entity-onboarding-details';
import FeaturePreviewAnnouncement from '@/components/feature-preview-announcement';
import { useEntityContext } from '@/entity/hooks/use-entity-context';
import Loading, { PanelSkeleton } from '../onboardings-shared/components/loading';
import { CURRENT_ONBOARDING_ID, ONBOARDINGS_ID } from '../onboardings-shared/constants';
import useGetOnboardings from '../onboardings-shared/hooks/use-get-onboardings';
import useShowOnboarding, { scrollToOnboarding } from '../onboardings-shared/hooks/use-show-onboarding';
import { getOnboardingByIdOrFallback } from '../onboardings-shared/utils';
import CurrentData from './components/current-data';
import OnboardingData from './components/onboarding-data';
import OnboardingStrip from './components/onboarding-strip';
import { ONBOARDING_PANEL_ID } from './constants';
import useOnboardingTitle from './hooks/use-onboarding-title';
import usePrefetchOnboardings from './hooks/use-prefetch-onboardings';
import useSectionScroll from './hooks/use-section-scroll';
import type { SectionKey } from './onboardings.types';
const Onboardings = () => {
  const {
    t
  } = useTranslation('entity-details', {
    keyPrefix: 'onboardings'
  });
  const {
    kind
  } = useEntityContext();
  const router = useRouter();
  const {
    isPending,
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError
  } = useGetOnboardings();
  const {
    onboardingId,
    onboardingSection,
    setOnboardingId
  } = useShowOnboarding();
  const getTitle = useOnboardingTitle();
  const hasHandledHashRef = useRef(false);
  // Pages are numbered, so an onboarding created after the first page loaded shifts the next one by a row.
  const onboardings = uniqBy(data?.pages.flatMap(page => page?.data ?? []) ?? [], 'id');
  const isCurrentSelected = onboardingId === CURRENT_ONBOARDING_ID;
  const requestedId = isCurrentSelected ? null : onboardingId;
  const selectedOnboarding = getOnboardingByIdOrFallback(onboardings, requestedId);
  const selectedId = isCurrentSelected ? CURRENT_ONBOARDING_ID : selectedOnboarding?.id;
  const isTargetLoaded = !requestedId || onboardings.some(onboarding => onboarding.id === requestedId);
  // TanStack keeps a failed page's error while its retry loads, so the retry reads as paging, not as the failure again.
  const isRetryingPage = isFetchNextPageError && isFetchingNextPage;
  const isPagingToTarget = !isTargetLoaded && hasNextPage && (!isFetchNextPageError || isRetryingPage);
  const isTargetUnreachable = !isTargetLoaded && isFetchNextPageError && !isRetryingPage;
  // The strip shows a pick at once; the panel, which renders every section, follows as a deferred update.
  const panelOnboarding = useDeferredValue(selectedOnboarding);
  const isPanelCurrent = useDeferredValue(isCurrentSelected);
  const panelId = isPanelCurrent ? CURRENT_ONBOARDING_ID : panelOnboarding?.id;
  const {
    stripRef,
    panelRef,
    sectionRef,
    openAt,
    openAtTop
  } = useSectionScroll(panelId, onboardingSection);
  const [stripHeight, setStripHeight] = useState<number>();

  // The Devices map sticks just below the strip, so the sections read its height.
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) {
      return;
    }
    const observer = new ResizeObserver(([entry]) => setStripHeight(entry.borderBoxSize[0]?.blockSize));
    observer.observe(strip);
    return () => observer.disconnect();
  }, [Boolean(selectedOnboarding)]);
  const {
    getIntentHandlers
  } = usePrefetchOnboardings({
    onboardings,
    selected: isCurrentSelected ? undefined : selectedOnboarding,
    // Until the panel catches up with the strip, the pick's requests haven't started and would read as settled.
    isEnabled: !isPagingToTarget && panelId === selectedId
  });
  const listProps = {
    onboardings,
    onSelect: (id: string) => {
      // On the way to a deep link the strip marks the fallback, which the panel isn't showing, so it can still be picked.
      if (id === selectedId && isTargetLoaded) {
        return;
      }
      openAtTop();
      setOnboardingId(id);
    },
    onLoadMore: () => fetchNextPage(),
    hasMore: hasNextPage,
    isLoadingMore: isFetchingNextPage,
    isLoadMoreError: isFetchNextPageError,
    getIntentHandlers
  };

  // A deep link can point at an onboarding past the first page, so keep paging until it shows up.
  // Once every page is loaded and it still isn't there, it belongs to another entity: fall back.
  // The audit trail reads the selection from the URL, so the fallback is written there too.
  useEffect(() => {
    if (!selectedOnboarding || isCurrentSelected || isFetchingNextPage || isTargetUnreachable) {
      return;
    }
    if (isPagingToTarget) {
      fetchNextPage();
    } else if (onboardingId !== selectedOnboarding.id) {
      setOnboardingId(selectedOnboarding.id);
    }
  }, [selectedOnboarding?.id, onboardingId, isCurrentSelected, isPagingToTarget, isFetchingNextPage, isTargetUnreachable]);

  // The link goes with the section it sits in, so focus moves to Current's matching section.
  const viewCurrent = (key: SectionKey) => {
    openAt(key);
    setOnboardingId(CURRENT_ONBOARDING_ID);
  };

  // Links like `?onboarding_id=…#onboardings` land here once that onboarding is on the page.
  useEffect(() => {
    if (hasHandledHashRef.current || isPending || isPagingToTarget) {
      return;
    }
    hasHandledHashRef.current = true;
    if (router.asPath.split('#')[1] === ONBOARDINGS_ID) {
      scrollToOnboarding();
    }
  }, [isPending, isPagingToTarget]);
  const renderPanel = () => {
    if (isPagingToTarget) {
      return <Loading />;
    }
    // A page failing on the way to a deep link keeps the link, rather than showing another onboarding in its place.
    if (isTargetUnreachable) {
      return <div className="p-5">
          <Card.Root>
            <Card.Empty>
              {t($ => $.list.loadOlderFailed)}{' '}
              <LinkButton className="align-baseline!" onClick={() => fetchNextPage()}>
                {t($_0 => $_0.list.tryAgain)}
              </LinkButton>
            </Card.Empty>
          </Card.Root>
        </div>;
    }
    if (isPanelCurrent) {
      return <>
          <header className="flex flex-col gap-1 px-5 pt-5 pb-4">
            <h3 className="text-heading-4 text-primary">{t($_1 => $_1.current.title)}</h3>
            <p className="text-body-2 text-tertiary">
              {kind === 'person' ? t($_2 => $_2.current.personDescription) : t($_3 => $_3.current.businessDescription)}
            </p>
          </header>
          <CurrentData sectionRef={sectionRef} />
        </>;
    }
    return panelOnboarding && <OnboardingData onboarding={panelOnboarding} onViewCurrent={viewCurrent} sectionRef={sectionRef} />;
  };

  // The page Section's header, built here so the title can stick with the strip.
  const header = <>
      <header className="mb-2 flex flex-wrap items-end justify-between gap-2">
        <div className="flex items-baseline gap-2">
          <h2 className="text-heading-5">{t($_4 => $_4.title)}</h2>
          {onboardings.length > 0 && <span className="text-body-2 text-tertiary">
              ({hasNextPage ? `${onboardings.length}+` : onboardings.length})
            </span>}
        </div>
      </header>
      <Divider className="mb-4" />
    </>;
  return <section aria-label={t($_5 => $_5.title)} id={ONBOARDINGS_ID} data-testid={ONBOARDINGS_ID} style={stripHeight ? {
    '--onboardings-strip-height': `${stripHeight}px`
  } as CSSProperties : undefined}>
      {!selectedOnboarding && header}
      {isPending && <PanelSkeleton />}
      {/* A failed "Show older" keeps the loaded panel; the list says it inline. */}
      {error && !data && <CardError error={error} />}
      {!isPending && !error && !selectedOnboarding && <div className="flex flex-col gap-4">
          <p className="text-body-2 text-primary">{t($_6 => $_6.noOnboardings, {
          kind
        })}</p>
          <div className="rounded-lg bg-secondary p-1">
            <section aria-label={t($_7 => $_7.current.title)} className="rounded-default border border-solid border-tertiary bg-primary pt-5">
              <CurrentData />
            </section>
          </div>
        </div>}
      {selectedOnboarding && <>
          {/* Above the title, which sticks. The block below reaches 28px up into the page's padding, so the gap keeps
           it off the banner and the 4px of shadow under it; empty once the banner is dismissed. */}
          <div className="empty:hidden md:pb-4">
            <FeaturePreviewAnnouncement preview="onboardings_strip" />
          </div>
          {/* From md the title and the strip stick together while the sections scroll under them. The block reaches up
           into the page's top padding and fills it, so stuck it sits where the page top would, and it looks the same
           stuck or not. */}
          <div ref={stripRef} className="z-10 flow-root bg-primary md:sticky md:-top-7 md:-mt-7 md:pt-7">
            {header}
            <div className="rounded-t-lg bg-secondary px-1 py-1">
              <OnboardingStrip {...listProps} selectedId={selectedId ?? selectedOnboarding.id} />
            </div>
            {/* The panel's top edge again, so stuck the sections scroll under a closed frame. It is see-through, and its
             shadow paints the tray outside the corners. The negative margin keeps it out of the block's measured height;
             flow-root stops that margin collapsing through the block. */}
            <div aria-hidden className="pointer-events-none -mb-2 hidden h-2 overflow-hidden px-1 md:block">
              <div className="h-full rounded-t-default border border-b-0 border-solid border-tertiary shadow-[0_0_0_8px_var(--background-color-secondary)]" />
            </div>
          </div>
          <div className="rounded-b-lg bg-secondary px-1 pb-1">
            {/* Isolated, so nothing inside, like a map pin, can rise above the strip. */}
            <section ref={panelRef} id={ONBOARDING_PANEL_ID} aria-label={isPanelCurrent || !panelOnboarding ? t($_8 => $_8.current.title) : getTitle(panelOnboarding)} className="isolate min-w-0 rounded-default border border-solid border-tertiary bg-primary">
              {renderPanel()}
            </section>
          </div>
        </>}
    </section>;
};
export default Onboardings;